import { z } from "zod";

export const annotationPresetSchema = z.object({
  tool: z.enum(["pen", "highlighter"]),
  color: z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/),
  size: z.number().int().min(1).max(8),
});

/** Geräteübergreifende Nutzereinstellungen (serverseitig gespeichert). */
export const userSettingsSchema = z.object({
  annotations: z
    .object({
      preset: annotationPresetSchema.nullable().catch(null),
      inputMode: z.enum(["pen-only", "both"]).catch("pen-only"),
    })
    .catch({ preset: null, inputMode: "pen-only" }),
  library: z
    .object({
      // „Zuletzt gespielt“ auf der Startseite: standardmäßig zugeklappt
      quickAccess: z.object({ recentOpen: z.boolean().catch(false) }).catch({ recentOpen: false }),
    })
    .catch({ quickAccess: { recentOpen: false } }),
});

export type UserSettings = z.infer<typeof userSettingsSchema>;
export type AnnotationPreset = z.infer<typeof annotationPresetSchema>;

export const DEFAULT_USER_SETTINGS: UserSettings = {
  annotations: { preset: null, inputMode: "pen-only" },
  library: { quickAccess: { recentOpen: false } },
};

/** Lädt gespeicherte Einstellungen tolerant (unbekannte/kaputte Felder → Defaults). */
export function parseUserSettings(input: unknown): UserSettings {
  const result = userSettingsSchema.safeParse(input ?? {});
  return result.success ? result.data : DEFAULT_USER_SETTINGS;
}

export const patchUserSettingsSchema = z.object({
  annotations: z
    .object({
      preset: annotationPresetSchema.nullable().optional(),
      inputMode: z.enum(["pen-only", "both"]).optional(),
    })
    .optional(),
  library: z
    .object({
      quickAccess: z.object({ recentOpen: z.boolean().optional() }).optional(),
    })
    .optional(),
});

export type PatchUserSettings = z.infer<typeof patchUserSettingsSchema>;
